type Prettify<T> = {
  [K in keyof T]: T[K];
  // eslint-disable-next-line @typescript-eslint/ban-types
} & {};

/**
 * Enumeration of the possible environments.
 * @enum {string}
 */
export enum Environment {
  Production = 'production',
  Sandbox = 'sandbox',
}

/**
 * Union type representing the environment values as strings.
 */
export type EnvironmentUnion = `${Environment}`;

/**
 * Translation keys for various messages used in the application.
 * @interface {Object} Translations
 */
export type Translations = {
  /**
   * The label for the bank account option.
   *
   * @defaultValue Bank Account
   */
  bank_account: string;

  /**
   * The label for the cancel button.
   *
   * @defaultValue Cancel
   */
  'button.cancel': string;

  /**
   * The label for the change button.
   *
   * @defaultValue Change
   */
  'button.change': string;

  /**
   * The label for the continue button.
   *
   * @defaultValue Continue
   */
  'button.continue': string;

  /**
   * Message shown when loading additional information.
   *
   * @defaultValue Loading
   */
  'button.loading': string;

  /**
   * The label for the card option.
   *
   * @defaultValue Card
   */
  card: string;

  /**
   * The label for the cardholder name input.
   *
   * @defaultValue Cardholder name
   */
  'card.card_holder_name': string;

  /**
   * The label for the card number input.
   *
   * @defaultValue Card number
   */
  'card.card_number': string;

  /**
   * Error message shown when the card number is incomplete.
   *
   * @defaultValue Your card number is incomplete
   */
  'card.card_number.invalid': string;

  /**
   * Error message shown when the card number comes from an unsupported card scheme.
   *
   * @defaultValue {CARD} not supported
   */
  'card.card_number.not_supported': string;

  /**
   * The label for the card expiry date input.
   *
   * @defaultValue Expiry date
   */
  'card.expiry_date': string;

  /**
   * Error message shown when the card expiry date is invalid.
   *
   * @defaultValue Your card's expiry date is in the past
   */
  'card.expiry_date.invalid': string;

  /**
   * Message describing the format for card expiry month input.
   *
   * @defaultValue MM
   */
  'card.expiry_date.placeholder.month': string;

  /**
   * Message describing the format for card expiry year input.
   *
   * @defaultValue YY
   */
  'card.expiry_date.placeholder.year': string;

  /**
   * The label for the preferred scheme selection.
   *
   * @defaultValue Choose your preferred scheme
   */
  'card.preferred_scheme.cta': string;

  /**
   * Message prompting the customer to select their preferred scheme.
   *
   * @defaultValue Your card offers two schemes, and you can select the one you prefer. Otherwise, the default choice applies.
   */
  'card.preferred_scheme.description': string;

  /**
   * The label for the card security code (CVV) input.
   *
   * @defaultValue Security code
   */
  'card.security_code': string;

  /**
   * Error message shown when the security code (CVV) is incomplete.
   *
   * @defaultValue Your card's security code is incomplete
   */
  'card.security_code.invalid': string;

  /**
   * Message describing the format for card security code input.
   *
   * @defaultValue CVV
   */
  'card.security_code.placeholder': string;

  /**
   * The label for the option to store card details for future purchases.
   *
   * @defaultValue Save card details for future purchases
   */
  'card.store_for_future_use.collect_consent': string;

  /**
   * Error message shown when the phone number is not registered.
   *
   * @defaultValue Phone number not registered
   */
  'customer.phone_number.not_registered': string;

  /**
   * The label for the edit button.
   *
   * @defaultValue Edit
   */
  'form.edit': string;

  /**
   * The label for the email input.
   *
   * @defaultValue Email address
   */
  'form.email': string;

  /**
   * Error message shown when the customer provides an invalid email.
   *
   * @defaultValue Must be a valid email
   */
  'form.email.invalid': string;

  /**
   * Placeholder message shown in the empty email input field.
   *
   * @defaultValue jordan.smith@email.com
   */
  'form.email.placeholder': string;

  /**
   * The label for the full name input.
   *
   * @defaultValue Full name
   */
  'form.full_name': string;

  /**
   * Placeholder message shown in the empty full name input field.
   *
   * @defaultValue Jordan Smith
   */
  'form.full_name.placeholder': string;

  /**
   * Error message shown when the customer inputs a value shorter than the required length.
   *
   * @defaultValue Must be greater than {VALUE} characters
   */
  'form.greater_than': string;

  /**
   * The label for the account holder first name input.
   *
   * @defaultValue Holder first name
   */
  'form.holder_first_name': string;

  /**
   * Placeholder message shown in the empty account holder first name input field.
   *
   * @defaultValue Jordan
   */
  'form.holder_first_name.placeholder': string;

  /**
   * The label for the account holder last name input.
   *
   * @defaultValue Holder last name
   */
  'form.holder_last_name': string;

  /**
   * Placeholder message shown in the empty account holder last name input field.
   *
   * @defaultValue Smith
   */
  'form.holder_last_name.placeholder': string;

  /**
   * Error message shown when the customer inputs a value longer than the required length.
   *
   * @defaultValue Must be less than {VALUE} characters
   */
  'form.less_than': string;

  /**
   * The label for the national ID input.
   *
   * @defaultValue National ID
   */
  'form.national_id': string;

  /**
   * Error message shown when the national ID is invalid.
   *
   * @defaultValue National ID is invalid
   */
  'form.national_id.invalid': string;

  /**
   * Placeholder message shown in the empty national ID input field.
   *
   * @defaultValue 30063412345
   */
  'form.national_id.placeholder': string;

  /**
   * The label for the phone country code input.
   *
   * @defaultValue Country code
   */
  'form.phone_country_code': string;

  /**
   * The label for the phone number input.
   *
   * @defaultValue Phone number
   */
  'form.phone_number': string;

  /**
   * The label for the phone number entry.
   *
   * @defaultValue Enter your phone number
   */
  'form.phone_number.cta': string;

  /**
   * Error message shown when the phone number is invalid.
   *
   * @defaultValue Must be a valid phone number
   */
  'form.phone_number.invalid': string;

  /**
   * Error message shown when the customer leaves a required field blank.
   *
   * @defaultValue This field is required
   */
  'form.required': string;

  /**
   * The label for the display Klarna options button.
   *
   * @defaultValue Show Klarna options
   */
  'klarna.show_options': string;

  /**
   * The label for the verification code entry.
   *
   * @defaultValue Enter your verification code
   */
  'otp.code': string;

  /**
   * The call to action message to capture the verification code sent to the customer’s contact details.
   *
   * @defaultValue Enter the verification code sent to <address>{ADDRESS}</address>
   */
  'otp.code.description': string;

  /**
   * The call to action message to capture the verification code sent to the customer’s email
   *
   * @defaultValue Enter the verification code sent to your email
   */
  'otp.code.description.email': string;

  /**
   * The call to action message to capture the verification code sent to the customer’s phone via SMS.
   *
   * @defaultValue Enter the verification code sent via SMS
   */
  'otp.code.description.phone': string;

  /**
   * The call to action message to capture the verification code sent to the customer’s phone via WhatsApp.
   *
   * @defaultValue Enter the verification code sent via WhatsApp
   */
  'otp.code.description.whatsapp': string;

  /**
   * The call to action message to send verification code via email.
   *
   * @defaultValue Send code via email
   */
  'otp.code.email': string;

  /**
   * Error message shown when the verification code is invalid.
   *
   * @defaultValue Incorrect verification code. Try again.
   */
  'otp.code.error': string;

  /**
   * Error message shown when the verification code expires.
   *
   * @defaultValue The code has expired. Request a new one.
   */
  'otp.code.expired': string;

  /**
   * Error message shown when the submitted verification code has an invalid amount of digits.
   *
   * @defaultValue Verification code must be {MIN}-{MAX} digits
   */
  'otp.code.length': string;

  /**
   * The call to action message to send verification code via SMS.
   *
   * @defaultValue Send code via SMS
   */
  'otp.code.phone': string;

  /**
   * Message shown to confirm the customer’s contact details where the verification code will be sent to.
   *
   * @defaultValue We'll send a verification code to <contact>{CONTACT}</contact>
   */
  'otp.code.send': string;

  /**
   * Error message shown when the customer verification fails.
   *
   * @defaultValue Failed to verify OTP
   */
  'otp.failed': string;

  /**
   * Error message shown after reaching the maximum resend limit for the verification code.
   *
   * @defaultValue Max resends reached
   */
  'otp.max_resends': string;

  /**
   * Error message shown after reaching the maximum number of verification code retries.
   *
   * @defaultValue Max retries reached
   */
  'otp.max_retries': string;

  /**
   * Message shown when the customer needs to wait before verification code can be resent.
   *
   * @defaultValue Resend a code in <countdown>{COUNTDOWN}</countdown>
   */
  'otp.resend.countdown': string;

  /**
   * The call to action message to resend verification code.
   *
   * @defaultValue Resend code
   */
  'otp.resend.cta': string;

  /**
   * Message shown to the customer if they want their verification code to be resent.
   *
   * @defaultValue Didn’t receive the code?
   */
  'otp.resend.description': string;

  /**
   * The call to action message to resend verification code via email.
   *
   * @defaultValue Send to email instead
   */
  'otp.resend.email': string;

  /**
   * The call to action message to resend verification code via SMS.
   *
   * @defaultValue Send to SMS instead
   */
  'otp.resend.phone': string;

  /**
   * The call to action message to send verification code via SMS.
   *
   * @defaultValue Send verification SMS
   */
  'otp.send.phone': string;

  /**
   * Message shown when the verification code is being sent via SMS.
   *
   * @defaultValue Sending verification SMS
   */
  'otp.sms.sending': string;

  /**
   * The label for the confirm button.
   *
   * @defaultValue Confirm
   */
  'pay_button.confirm': string;

  /**
   * Message shown when confirming details.
   *
   * @defaultValue Confirming
   */
  'pay_button.confirming': string;

  /**
   * The call to action shown on the pay button.
   *
   * @defaultValue Pay
   */
  'pay_button.pay': string;

  /**
   * The payment method specific call to action shown on the pay button.
   *
   * @defaultValue Pay with {PAYMENT_METHOD}
   */
  'pay_button.pay_with': string;

  /**
   * Message shown when the payment is completed.
   *
   * @defaultValue Payment complete
   */
  'pay_button.payment_complete': string;

  /**
   * Message shown while the payment is processing.
   *
   * @defaultValue Payment processing
   */
  'pay_button.payment_processing': string;

  /**
   * Message shown before the customer is redirected to complete their payment.
   *
   * @defaultValue After clicking on Pay, you are redirected to finalize your payment
   */
  'pay_button.redirect_cta': string;

  /**
   * The call to action message to approve payment in the mobile application of the payment method.
   *
   * @defaultValue Approve payment on the {PAYMENT_METHOD} app
   */
  'payment.approval': string;

  /**
   * The label for the button confirming that the customer cancelled the payment.
   *
   * @defaultValue I have canceled this payment
   */
  'payment.approval.cancel_cta': string;

  /**
   * The label for the button confirming that the customer approved the payment.
   *
   * @defaultValue I have approved this payment
   */
  'payment.approval.confirm_cta': string;

  /**
   * Error message shown when the customer provides a card that is not supported.
   *
   * @defaultValue Card not supported
   */
  'payment.declined.card_not_supported': string;

  /**
   * Error message shown when the customer provides invalid payment details.
   *
   * @defaultValue Incorrect details – try again or use another payment method
   */
  'payment.declined.invalid_customer_data': string;

  /**
   * Error message shown when the `PaymentSession` data provided is invalid for payment method you are trying to process.
   *
   * @defaultValue Try another payment method
   */
  'payment.declined.invalid_payment_session_data': string;

  /**
   * Error message shown when your account is not configured for the payment method you are trying to process.
   *
   * @defaultValue Payment declined – try another payment method
   */
  'payment.declined.merchant_misconfiguration': string;

  /**
   * Error message shown when the customer does not hold sufficient funds to complete the transaction with their chosen payment method.
   *
   * @defaultValue Not enough funds – try another payment method
   */
  'payment.declined.not_enough_funds': string;

  /**
   * Error message shown when the payment is cancelled.
   *
   * @defaultValue Payment cancelled
   */
  'payment.declined.payment_cancelled': string;

  /**
   * Error message shown when the payment request expires.
   *
   * @defaultValue Payment expired
   */
  'payment.declined.payment_expired': string;

  /**
   * Fallback message shown when the chosen payment method declines the transaction.
   *
   * @defaultValue Payment declined – try again or use another payment method
   */
  'payment.declined.try_again': string;

  /**
   * Message shown when the payment is successful.
   *
   * @defaultValue Payment successful
   */
  'payment.successful': string;

  /**
   * Error message shown after failing to load the payment method.
   */
  'payment_context.error': string;

  /**
   * The label for the SEPA Direct Debit creditor name.
   *
   * @defaultValue Creditor
   */
  'sepa.creditor': string;

  /**
   * The label for the SEPA Direct Debit creditor ID.
   *
   * @defaultValue Creditor ID
   */
  'sepa.creditor_id': string;

  /**
   * The label for the International Bank Account Number (IBAN) input.
   *
   * @defaultValue IBAN
   */
  'sepa.iban': string;

  /**
   * Error message shown when the International Bank Account Number (IBAN) is invalid.
   *
   * @defaultValue Please enter a valid IBAN. Your IBAN should start with two letters and be 22 characters long
   */
  'sepa.iban.invalid': string;

  /**
   * Placeholder message shown in the empty International Bank Account Number (IBAN) input.
   *
   * @defaultValue AB12 1234 1234 1234 1234 12
   */
  'sepa.iban.placeholder': string;

  /**
   * The label for the stored card options.
   *
   * @defaultValue Saved cards
   */
  stored_card: string;

  /**
   * The label for the use new card option displayed next to stored card options.
   *
   * @defaultValue Use a new card
   */
  'stored_card.card': string;

  /**
   * The label for other payment method options when stored cards are available.
   *
   * @defaultValue Other payment methods
   */
  'stored_card.other_payment_methods': string;

  /**
   * The label for the default option.
   *
   * @defaultValue Default
   */
  'tag.default': string;

  /**
   * The label for an expired option.
   *
   * @defaultValue Expired
   */
  'tag.expired': string;
};

/**
 * Type representing custom translations for different locales.
 * @interface CustomTranslations
 * @property {Partial<Translations>} [locale] - Custom translations for a specific locale (examples `en` or `en-GB`).
 */
export type CustomTranslations = {
  /**
   * Custom translations for a specific locale (examples `en` or `en-GB`).
   */
  [locale: string]: Partial<Translations>;
};

/**
 * Enumeration of the supported component names.
 * @enum {string}
 */
export enum ComponentName {
  AlipayCn = 'alipay_cn',
  AlipayHk = 'alipay_hk',
  Alma = 'alma',
  Applepay = 'applepay',
  Bancontact = 'bancontact',
  Benefit = 'benefit',
  Bizum = 'bizum',
  Blik = 'blik',
  Card = 'card',
  Dana = 'dana',
  Eps = 'eps',
  Gcash = 'gcash',
  Googlepay = 'googlepay',
  Ideal = 'ideal',
  Kakaopay = 'kakaopay',
  Klarna = 'klarna',
  Knet = 'knet',
  Mbway = 'mbway',
  MobilePay = 'mobilepay',
  Multibanco = 'multibanco',
  Octopus = 'octopus',
  P24 = 'p24',
  PayNow = 'paynow',
  Paypal = 'paypal',
  Plaid = 'plaid',
  Qpay = 'qpay',
  RememberMe = 'remember_me',
  Sepa = 'sepa',
  StcPay = 'stcpay',
  StoredCard = 'stored_card',
  Swish = 'swish',
  Tabby = 'tabby',
  Tamara = 'tamara',
  TouchNGo = 'tng',
  Truemoney = 'truemoney',
  Twint = 'twint',
  Vipps = 'vipps',
  WeChatPay = 'wechatpay',
  Flow = 'flow',

  Authentication = 'authentication',
  CardCvv = 'card_cvv',
  ShippingAddress = 'shipping_address',
}

/**
 * Union type representing the component name values as strings.
 */
export type ComponentNameUnion = `${ComponentName}`;

/**
 * Enumeration of card schemes.
 * @enum {string}
 */
export enum CardScheme {
  Amex = 'american_express',
  CartesBancaires = 'cartes_bancaires',
  ChinaUnionPay = 'china_union_pay',
  DinersClub = 'diners_club_international',
  Discover = 'discover',
  JCB = 'jcb',
  Mada = 'mada',
  Mastercard = 'mastercard',
  Visa = 'visa',
}

/**
 * Enumeration of card types.
 * @enum {string}
 */
export enum CardType {
  Charge = 'charge',
  Credit = 'credit',
  Debit = 'debit',
  DeferredDebit = 'deferred_debit',
  Prepaid = 'prepaid',
}

/**
 * [The Card Metadata lookup response](https://api-reference.checkout.com/#operation/requestCardMetadata!c=200&path=bin&t=response).
 * @interface CardMetadataLookupResponse
 */
export type CardMetadataLookupResponse = Prettify<
  {
    bin: string;
    card_type?: string;
    local_schemes?: string[];
    scheme: string;
  } & Record<string, unknown>
>;

/**
 * [The Card Tokenization response](https://api-reference.checkout.com/#operation/requestAToken!c=201&path=0/type&t=response).
 * @interface CardTokenizationResponse
 */
export type CardTokenizationResponse = Prettify<
  {
    bin: string;
    scheme?: string;
    token: string;
    type: 'card';
  } & Record<string, unknown>
>;

/**
 * The card tokenization result and metadata.
 * @interface CardTokenizeResult
 */
export type CardTokenizeResult = {
  /**
   * The name of the component that triggered the tokenization. Only `card` is supported.
   */
  type: ComponentName.Card;

  /**
   * [The Card Tokenization response](https://api-reference.checkout.com/#operation/requestAToken!c=201&path=0/type&t=response).
   */
  data: CardTokenizationResponse;

  /**
   * An optional [Card Metadata lookup response](https://api-reference.checkout.com/#operation/requestCardMetadata!c=200&path=bin&t=response) object included when a Card Metadata API response is received during customer input.
   */
  cardMetadata?: CardMetadataLookupResponse;
};

export type CardTokenizeResultWithRiskDeviceSessionId = Prettify<
  CardTokenizeResult & {
    /**
     * An optional [risk assessment configuration](https://api-reference.checkout.com/#operation/requestAPaymentOrPayout!path=0/risk&t=request) object
     */
    risk?: {
      /**
       * Device session ID collected from the [Risk.js package](https://www.checkout.com/docs/business-operations/prevent-fraud/integrate-with-risk-sdk).
       */
      device_session_id: string;
    };
  }
>;

/**
 * [The Card CVV Tokenization response](https://api-reference.checkout.com/#operation/requestAToken!c=201&path=3/type&t=response).
 * @interface CardCvvTokenizationResponse
 */
export type CardCvvTokenizationResponse = {
  expires_on: string;
  token: string;
  type: 'cvv';
};

/**
 * The card CVV tokenization result.
 * @interface CardCvvTokenizeResult
 */
export type CardCvvTokenizeResult = {
  /**
   * The name of the component that triggered the tokenization. Only `card_cvv` is supported.
   */
  type: ComponentName.CardCvv;

  /**
   * [The Card CVV Tokenization response](https://api-reference.checkout.com/#operation/requestAToken!c=201&path=3/type&t=response).
   */
  data: CardCvvTokenizationResponse;
};

/**
 * The tokenization result information for `.tokenize()` method.
 * @interface TokenizeResult
 */
export type TokenizeResult = CardTokenizeResultWithRiskDeviceSessionId | CardCvvTokenizeResult;

/**
 * The tokenization result information for `onTokenized` callback.
 * @interface OnTokenizedResult
 */
export type OnTokenizedResult = CardTokenizeResult | CardCvvTokenizeResult;

/**
 * Callback response to proceed with payment.
 * @interface ProceedPaymentResponse
 */
export type ProceedPaymentResponse = {
  /**
   * Specifies whether to continue with the payment process.
   */
  continue: true;
};

/**
 * Callback response to abort payment.
 * @interface AbortPaymentResponse
 */
export type AbortPaymentResponse = {
  /**
   * Specifies whether to continue with the payment process.
   */
  continue: false;
};

/**
 * Callback response to abort payment with error message.
 * @interface AbortPaymentWithErrorResponse
 */
export type AbortPaymentWithErrorResponse = Prettify<
  AbortPaymentResponse & {
    /**
     * An optional error message to display to the customer.
     */
    errorMessage?: string;
  }
>;

/**
 * The display mode of the stored cards.
 * @enum {StoredCardDisplayMode}
 */
export enum StoredCardDisplayMode {
  /**
   * Only the default payment method is displayed.
   */
  DefaultOnly = 'default_only',

  /**
   * All payment methods are displayed.
   */
  All = 'all',
}

/**
 * The display position of the payment method's additional content.
 */
export enum DisplayPaymentAdditionalContent {
  AbovePayButton = 'above_pay_button',
  BelowPayButton = 'below_pay_button',
}

/**
 * Callback to handle mounting a payment method's additional content.
 */
export type HandlePaymentAdditionalContentMount = (
  component: Component,
  element: Element
) => Promise<string | void>;

/**
 * Callback to handle unmounting a payment method's additional content.
 */
export type HandlePaymentAdditionalContentUnmount = (
  component: Component,
  element: Element,
  mountIdentifier: string | undefined
) => Promise<void>;

/**
 * Response for the `onCardBinChanged` callback.
 */
export type OnCardBinChangedResponse = Prettify<
  ProceedPaymentResponse | AbortPaymentWithErrorResponse
>;

/**
 * Response for the `onTokenized` callback.
 */
export type OnTokenizedResponse = Prettify<ProceedPaymentResponse | AbortPaymentWithErrorResponse>;

/**
 * Response for the `onAuthorized` callback.
 */
export type OnAuthorizedResponse = Prettify<ProceedPaymentResponse | AbortPaymentWithErrorResponse>;

/**
 * Data for submitting customized payment session request.
 */
export type SubmitData = {
  /**
   * A unique token representing the additional customer data that Flow captured. You must provide this token when you submit the customized payment session request.
   */
  session_data: string;
};

/**
 * Response for the `handleSubmit` callback.
 */
export type HandleSubmitResponse = Prettify<
  {
    /**
     * A unique identifier for the payment.
     */
    id: string;

    /**
     * The payment status.
     */
    status: string;

    /**
     * The name of the payment method that was used for the payment.
     */
    type: ComponentNameUnion;
  } & Record<string, unknown>
>;

/**
 * Response for the `handleClick` callback.
 */
export type HandleClickResponse = Prettify<ProceedPaymentResponse | AbortPaymentResponse>;

/**
 * Interface representing a component in the checkout flow.
 * @interface Component
 */
export interface Component {
  /** The type of the component. */
  type: ComponentNameUnion;

  /** The type of the selected payment method in the Flow or Stored Card component. For other components, this is the same as `type`. */
  selectedType: ComponentNameUnion;

  /** The identifier of the selected payment method in the Flow or Stored Card component. Returns `undefined` when no payment method is selected. */
  selectedPaymentMethodId: string | undefined;

  /** The display name of the component. */
  name: string;

  /**
   * Submits a component, if you require your own pay button.
   * Not available for digital wallets, including Apple Pay and Google Pay.
   */
  submit(): void;

  /**
   * This method must be used with your own pay button. It submits a component element with card tokenization, if the selected component supports it. Otherwise, it throws an error with `method_not_supported` code. Returns a [JavaScript Promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise) that resolves with the `TokenizeResult` object.
   *
   * @return {Promise<TokenizeResult | void>} The tokenization result.
   */
  tokenize(): Promise<TokenizeResult | void>;

  /**
   * Checks if the component has captured all details from the customer and can be submitted.
   *
   * @return {boolean} Validity of the component for submission.
   */
  isValid(): boolean;

  /**
   * Checks if the component can be rendered. Call this before mounting the component.
   *
   * @return {Promise<boolean>} Promise resolving with availability of the component.
   */
  isAvailable(): Promise<boolean>;

  /**
   * Checks if the component requires a pay button to trigger payment submission, if you require your own pay button.
   *
   * For the Flow component, this method returns the information about the selected payment method.
   *
   * @return {boolean} Whether a pay button is required for the selected payment method.
   */
  isPayButtonRequired(): boolean;

  /**
   * Cancels the selection of the currently selected payment method in the component instance.
   *
   * This method only works with the Flow component and `stored_card` payment method. Calling `unselect()` when no payment method is selected has no effect.
   *
   * Additionally, after calling `unselect()`, subsequent calls to `submit()` or `tokenize()` also have no effect, as no payment method is active.
   */
  unselect(): void;

  /**
   * Mounts a component to a page. You can either provide:
   * - a [DOM Element Object](https://developer.mozilla.org/en-US/docs/Web/API/Document_object_model/Locating_DOM_elements_using_selectors)
   * - an [Element Selector](https://developer.mozilla.org/en-US/docs/Web/API/Element)
   *
   * @param {string | Element} element - The Element Selector or DOM Element object to mount to
   * @return {Component} - The mounted component.
   */
  mount(element: string | Element): Component;

  /**
   * Unmounts a component from the page.
   *
   * This stops the component rendering, and removes all life-cycles from the browser.
   *
   * @return {Component} - The unmounted component.
   */
  unmount(): Component;
}

/**
 * Information on a successful payment.
 * @interface PayPaymentSessionSuccessfulResponse
 */
export type PayPaymentSessionSuccessfulResponse = {
  /**
   * A unique identifier for the payment.
   */
  id: string;

  /**
   * The payment status. This value will always return `Approved`.
   */
  status: 'Approved';

  /**
   * The name of the payment method that was used for the payment.
   */
  type: ComponentNameUnion;
};

/**
 * Enumeration of possible types of checkout errors.
 * @enum {string}
 */
export enum CheckoutErrorType {
  Integration = 'Integration',
  Request = 'Request',
  PaymentMethod = 'PaymentMethod',
  Submit = 'Submit',
  Validation = 'Validation',
}

/**
 * Enumeration of error codes for payment method errors.
 * @enum {string}
 */
export enum CheckoutPaymentMethodErrorCode {
  /**
   * The payment attempt for the payment method specified in `details.type` failed due to an unexpected failure.
   *
   * Unexpected failures may occur when Flow integrates with a payment method's client-side library, or makes network requests to the payment method's endpoints.
   *
   * If you persistently encounter the error for a specific payment method, this could be caused by your account configuration. For example, incomplete onboarding to a payment method. Contact your account manager or support@checkout.com.
   */
  PaymentMethodAttemptFailed = 'payment_method_attempt_failed',
}

/**
 * Enumeration of error codes for integration errors.
 * @enum {string}
 */
export enum CheckoutIntegrationErrorCode {
  /**
   * Flow was loaded in a non-browser environment.
   *
   * You should only load and execute Flow client-side.
   *
   * If your integration involves server-side rendering or automated testing, ensure the [Window](https://developer.mozilla.org/en-US/docs/Web/API/Window) object exists before you load Flow.
   */
  BrowserEnvironmentRequired = 'browser_environment_required',

  /**
   * The callback you provided failed during invocation.
   *
   * If you provide a custom callback, ensure error handling is implemented correctly.
   */
  CallbackInvocationFailed = 'callback_invocation_failed',

  /**
   * The result returned in a callback execution was invalid.
   *
   * If you provide a custom callback, ensure it returns a result.
   *
   * Flow supports `Promise` returned in callbacks, in addition to direct results.
   */
  CallbackResultInvalid = 'callback_result_invalid',

  /**
   * Flow has already been mounted to DOM with `mount()` and cannot be mounted again.
   *
   * If you need to mount Flow again because the checkout page changed, use the `unmount()` method first before you call `mount()` again.
   */
  ComponentAlreadyMounted = 'component_already_mounted',

  /**
   * The `isAvailable()` method was not called before mounting the component with `mount()`. The component may not be supported by the customer device.
   *
   * After you create the component, call the `isAvailable()` method to verify that the payment method is supported by the customer device. This does not apply to a component of type `flow`.
   */
  ComponentIsAvailableNotChecked = 'component_is_available_not_checked',

  /**
   * The name specified when creating the component using the `create()` method is not supported.
   *
   * If you are providing `enabled_payment_methods` or `disabled_payment_methods` options when you [create the payment session](https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/get-started-with-flow#Step_1:_Create_a_new_Payment_Session_), ensure you also include the payment method that you want to offer.
   *
   * You should also ensure that the `currency` and `billing.address.country` values provided to the payment session match the payment method's requirements.
   */
  ComponentNotSupported = 'component_not_supported',

  /**
   * The [Element selector](https://developer.mozilla.org/en-US/docs/Web/API/Element) or [DOM object](https://developer.mozilla.org/en-US/docs/Web/API/Document_object_model/Locating_DOM_elements_using_selectors) you provided when calling the `mount()` method could not be found.
   *
   * Ensure the Element selector you provide to the `mount()` method is already mounted to the DOM. Alternatively, you can query for the DOM object and provide the reference when you call `mount()`.
   */
  ElementNotFound = 'element_not_found',

  /**
   * The method you called is not supported by the component.
   *
   * For example, the `tokenize()` method can only be called by the card component, and will fail if called by any other component.
   */
  MethodNotSupported = 'method_not_supported',

  /**
   * The payment session response provided when initializing `CheckoutWebComponents` was invalid.
   *
   * After you create a payment session in your server-side integration, ensure you return the entire payment session response object to the client-side and provide it to `CheckoutWebComponents`.
   */
  PaymentSessionInvalid = 'payment_session_invalid',

  /**
   * The payment session response is not provided when initializing `CheckoutWebComponents`.
   *
   * After you create a payment session in your server-side integration, ensure you return the entire payment session response object to the client-side and provide it to `CheckoutWebComponents`.
   */
  PaymentSessionRequired = 'payment_session_required',
}

/**
 * Enumeration of error codes for request errors.
 * @enum {string}
 */
export enum CheckoutRequestErrorCode {
  /**
   * The payment request was aborted, e.g. you returned `{ continue: false }` response in the `onCardBinChanged` or `onTokenized` callback.
   */
  PaymentRequestAborted = 'payment_request_aborted',

  /**
   * The payment request was declined.
   *
   * The payment's ID is provided in the `details.paymentId` property. You can fetch the details of the payment by performing a [Get payment details](https://www.checkout.com/docs/payments/manage-payments/get-payment-details) request or searching for the payment record in the Dashboard.
   *
   * The `details.requestErrorCodes` property will contain one of the values in `PaymentDeclineReason` enum.
   *
   * Flow automatically displays error messages to the customer, with suggested next actions to improve retry success.
   */
  PaymentRequestDeclined = 'payment_request_declined',

  /**
   * The payment request failed due to unexpected error or network issue.
   *
   * Any additional error codes from the network requests will be provided in the `details.requestErrorCodes` property. See the possible [error codes](https://www.checkout.com/docs/developer-resources/codes/error-codes#Payments_endpoint) for the Payment endpoint for more details.
   *
   * Customers can typically complete their payment after a retry.
   *
   * If you persistently encounter the error for a specific payment method, this could be caused by your account configuration. Contact your account manager or support@checkout.com.
   */
  PaymentRequestFailed = 'payment_request_failed',

  /**
   * The request failed due to unexpected error or network issue.
   *
   * Any additional error codes from the network requests will be provided in the `details.requestErrorCodes` property.
   *
   * Customers can typically complete their payment after a retry.
   *
   * If you persistently encounter the error for a specific payment method, this could be caused by your account configuration. Contact your account manager or support@checkout.com.
   */
  NetworkError = 'network_error',

  /**
   * The tokenization request failed due to unexpected error or network issue.
   *
   * Customers can typically complete tokenization after a retry.
   *
   * If you persistently encounter the error for a specific payment method, this could be caused by your account configuration. Contact your account manager or support@checkout.com.
   */
  TokenizeRequestFailed = 'tokenize_request_failed',
}

/**
 * Enumeration of error codes for submit errors.
 * @enum {string}
 */
export enum CheckoutSubmitErrorCode {
  /**
   * The payment request was submitted with the `submit()` method before the customer had provided all of the details required by the chosen payment method.
   *
   * If you're rendering a custom pay button and passed `showPayButton: false` when you created the component, check that the customer inputs are valid with the `isValid()` method before you submit a payment request.
   */
  ComponentInvalid = 'component_invalid',
}

/**
 * Enumeration of error codes for validation errors.
 * @enum {string}
 */
export enum CheckoutValidationErrorCode {
  /**
   * The parameters passed to `update()` were invalid.
   *
   * The `details.validationErrors` property contains a map of invalid field names to human-readable reasons. Unknown keys produce the reason `"not supported field"`; invalid `amount` or `currency` values produce field-specific reasons.
   *
   * Ensure `amount` is a non-negative integer in minor units, `currency` is a 3-character uppercase ISO 4217 code, and no unsupported fields are passed.
   */
  UpdateParametersInvalid = 'update_parameters_invalid',
}

/**
 * Base type for checkout errors.
 * @interface CheckoutErrorBase
 */
type CheckoutErrorBase = {
  /**
   * The error class name.
   */
  name: 'CheckoutError';

  /**
   * A human-readable description of the error.
   *
   * The description is subject to change. For example, to improve clarity. To ensure you catch errors correctly, you must reference the values returned by the type and code fields instead.
   */
  message: string;
};

/**
 * Integration errors are caused by an invalid use of `CheckoutWebComponents`.
 * @interface CheckoutIntegrationError
 */
export type CheckoutIntegrationError = Prettify<
  CheckoutErrorBase & {
    /**
     * The type of error encountered.
     */
    type: `${CheckoutErrorType.Integration}`;

    /**
     * A code representing the error.
     */
    code: CheckoutIntegrationErrorCode;

    /**
     * Additional error properties.
     */
    details: {
      /**
       * The unique identifier of the browser session that created the component.
       */
      browserSessionId: string;

      /**
       * The Element selector or DOM object that caused the mounting error.
       *
       * This property is only returned for errors with an `element_not_found` error code.
       */
      element?: string | Element;

      /**
       * The unique identifier of the provided payment session response.
       */
      paymentSessionId?: string;

      /**
       * The name of the payment method used for the payment.
       */
      type?: ComponentNameUnion;
    };
  }
>;

/**
 * Enumeration of possible reasons for payment decline.
 * @enum {string}
 */
export enum PaymentDeclineReason {
  NotEnoughFunds = 'not_enough_funds',
  InvalidPaymentSessionData = 'invalid_payment_session_data',
  InvalidCustomerData = 'invalid_customer_data',
  CustomerMisconfiguration = 'customer_misconfiguration',
  MerchantMisconfiguration = 'merchant_misconfiguration',
  TryAgain = 'try_again',
  PaymentCancelled = 'payment_cancelled',
  PaymentExpired = 'payment_expired',
}

/**
 * Enumeration of possible reasons for payment abortion.
 * @enum {string}
 */
export enum PaymentAbortReason {
  CardNotSupported = 'card_not_supported',
}

/**
 * Request errors are caused by network request failures.
 * @interface CheckoutRequestError
 */
export type CheckoutRequestError = Prettify<
  CheckoutErrorBase & {
    /**
     * The type of error encountered.
     */
    type: `${CheckoutErrorType.Request}`;

    /**
     * A code representing the error.
     */
    code: CheckoutRequestErrorCode | CheckoutPaymentMethodErrorCode;

    /**
     * Additional error properties.
     */
    details: {
      /**
       * The unique identifier of the browser session that created the component.
       */
      browserSessionId?: string;

      /**
       * The unique identifier of the payment.
       */
      paymentId?: string;

      /**
       * The unique identifier of the provided payment session response.
       */
      paymentSessionId?: string;

      /**
       * Additional error codes returned from the network request.
       */
      requestErrorCodes?: (PaymentDeclineReason | string)[];

      /**
       * The unique identifier for the network request.
       */
      requestId?: string;

      /**
       * The [HTTP response status code](https://www.checkout.com/docs/developer-resources/codes/api-response-codes#HTTP_status_codes) for the network request.
       */
      status?: number;

      /**
       * The name of the payment method used for the payment.
       */
      type?: ComponentNameUnion;

      /**
       * Custom error message provided when the error occurred.
       */
      customMessage?: string;

      /**
       * The reason for the payment abortion.
       */
      abortReason?: PaymentAbortReason;
    };
  }
>;

/**
 * Payment method errors are caused by unexpected failures when integrating a payment method's client-side library.
 * @interface CheckoutPaymentMethodError
 */
export type CheckoutPaymentMethodError = Prettify<
  CheckoutErrorBase & {
    /**
     * The type of error encountered.
     */
    type: `${CheckoutErrorType.PaymentMethod}`;

    /**
     * A code representing the error.
     */
    code: CheckoutPaymentMethodErrorCode;

    /**
     * Additional error properties.
     */
    details: {
      /**
       * The unique identifier of the browser session that created the Component.
       */
      browserSessionId: string;

      /**
       * The unique identifier of the provided payment session response.
       */
      paymentSessionId: string;

      /**
       * The name of the payment method used for the payment.
       */
      type: ComponentNameUnion;
    };
  }
>;

/**
 * Submit errors are caused by invalid attempts to submit a payment request. For example, if you call the `submit()` method before the customer has completed all of the required input fields.
 * @interface CheckoutSubmitError
 */
export type CheckoutSubmitError = Prettify<
  CheckoutErrorBase & {
    /**
     * The type of error encountered.
     */
    type: `${CheckoutErrorType.Submit}`;

    /**
     * A code representing the error.
     */
    code: CheckoutSubmitErrorCode | CheckoutPaymentMethodErrorCode;

    /**
     * Additional error properties.
     */
    details: {
      /**
       * The unique identifier of the browser session that created the Component.
       */
      browserSessionId: string;

      /**
       * The unique identifier of the provided payment session response.
       */
      paymentSessionId: string;

      /**
       * The name of the payment method used for the payment.
       */
      type: ComponentNameUnion;
    };
  }
>;

/**
 * Validation errors are caused by invalid parameters passed to `CheckoutWebComponents` methods, such as `update()`.
 * @interface CheckoutValidationError
 */
export type CheckoutValidationError = Prettify<
  CheckoutErrorBase & {
    /**
     * The type of error encountered.
     */
    type: `${CheckoutErrorType.Validation}`;

    /**
     * A code representing the error.
     */
    code: CheckoutValidationErrorCode;

    /**
     * Additional error properties.
     */
    details: {
      /**
       * The unique identifier of the browser session that created `CheckoutWebComponents`.
       */
      browserSessionId?: string;

      /**
       * The unique identifier of the provided payment session response.
       */
      paymentSessionId?: string;

      /**
       * The name of the payment method used for the payment, if the error originated from a specific component.
       */
      type?: ComponentNameUnion;

      /**
       * A map of invalid field names to human-readable reasons describing why validation failed.
       *
       * For example, passing an unsupported key to `update()` will produce `{ <fieldName>: 'not supported field' }`.
       */
      validationErrors: Record<string, string>;
    };
  }
>;

/**
 * Union type representing any checkout error.
 * @typedef {CheckoutIntegrationError | CheckoutRequestError | CheckoutPaymentMethodError | CheckoutSubmitError | CheckoutValidationError} CheckoutError
 */
export type CheckoutError =
  | CheckoutIntegrationError
  | CheckoutRequestError
  | CheckoutPaymentMethodError
  | CheckoutSubmitError
  | CheckoutValidationError;

/**
 * Type representing supported dimensions which can be a string or number.
 */
export type SupportedDimensions = string | number;

/**
 * Interface representing font properties.
 * @interface Font
 */
export interface Font {
  /** The font family name. */
  fontFamily: string;

  /** The font size. */
  fontSize: SupportedDimensions;

  /** The line height. */
  lineHeight: SupportedDimensions;

  /** The font weight. */
  fontWeight: number;

  /** The letter spacing. */
  letterSpacing: SupportedDimensions;
}

/**
 * Type representing color tokens.
 * @interface ColorTokens
 */
export type ColorTokens = {
  /** Primary color. */
  colorPrimary: string;

  /** Secondary color. */
  colorSecondary: string;

  /** Disabled state color. */
  colorDisabled: string;

  /** Error state color. */
  colorError: string;

  /** Inverse color. */
  colorInverse: string;

  /** Action color. */
  colorAction: string;

  /** Success state color. */
  colorSuccess: string;

  /** Background color. */
  colorBackground: string;

  /** Form background color. */
  colorFormBackground: string;

  /** Border color. */
  colorBorder: string;

  /** Form border color. */
  colorFormBorder: string;

  /** Outline color. */
  colorOutline: string;
};

/**
 * Type representing border radius which is a tuple of supported dimensions.
 */
export type BorderRadius = [SupportedDimensions, SupportedDimensions];

/**
 * Enumeration of font names.
 * @enum {string}
 */
export enum FontName {
  Subheading = 'subheading',
  Footnote = 'footnote',
  Button = 'button',
  Input = 'input',
  Label = 'label',
}

/**
 * Type representing design tokens.
 * @interface DesignTokens
 */
export type DesignTokens = Prettify<
  {
    /**
     * Subheading font properties.
     */
    subheading: Partial<Font>;

    /**
     * Footnote font properties.
     */
    footnote: Partial<Font>;

    /**
     * Button font properties.
     */
    button: Partial<Font>;

    /**
     * Input font properties.
     */
    input: Partial<Font>;

    /**
     * Label font properties.
     */
    label: Partial<Font>;

    /**
     * Border radius properties.
     */
    borderRadius: BorderRadius;

    /**
     * Border width.
     */
    borderWidth: string;

    /**
     * Focus outline width.
     */
    focusOutlineWidth: string;
  } & ColorTokens
>;

/**
 * Type representing partial design tokens.
 */
export type PartialDesignTokens = Partial<DesignTokens>;

/**
 * Shared options for configuring different components.
 * @interface SharedComponentOptions
 */
type SharedComponentOptions = {
  /**
   * Raised when a component is initialized and ready for the customer to interact.
   *
   * @param {Component} component - The component that fired the event.
   */
  onReady?: (component: Component) => void;

  /**
   * Raised when a component changes after a customer interaction.
   *
   * @param {Component} component - The component that fired the event.
   */
  onChange?: (component: Component) => void;

  /**
   * Raised when the customer inputs or changes the first 8 digits of a card.
   *
   * You can use this event to perform checks on a card based on its metadata or to observe changes to the BIN.
   *
   * Flow accepts the card if your provided callback doesn't return a response, or you can specify whether to accept or reject the card.
   *
   * @param {Component} component - The component that fired the event.
   * @param {CardMetadataLookupResponse} cardMetadata - The Card Metadata lookup response.
   * @return {Promise<OnCardBinChangedResponse | void>} - Whether to continue with the payment process.
   */
  onCardBinChanged?: (
    component: Component,
    cardMetadata: CardMetadataLookupResponse
  ) => Promise<OnCardBinChangedResponse | void>;

  /**
   * Raised when you or the customer have triggered a component submission.
   *
   * @param {Component} component - The component that fired the event.
   */
  onSubmit?: (component: Component) => void;

  /**
   * Raised when the cardholder details provided by the customer have been tokenized.
   *
   * You can use this event to perform checks on a card based on its tokenization result and metadata.
   *
   * Flow accepts the card if your provided callback doesn't return a response, or you can specify whether to accept or reject the card.
   *
   * @param {Component} component - The component that fired the event.
   * @param {OnTokenizedResult} tokenizeResult - The tokenization result.
   * @return {Promise<OnTokenizedResponse | void>} - Whether to continue with the payment process.
   */
  onTokenized?: (
    component: Component,
    tokenizeResult: OnTokenizedResult
  ) => Promise<OnTokenizedResponse | void>;

  /**
   * Raised when the payment has been completed synchronously.
   *
   * This event will not be fired if the payment requires asynchronous action. For example, 3DS authentication.
   *
   * @param {Component} component - The component that fired the event.
   * @param {PayPaymentSessionSuccessfulResponse} payment - The successful payment response.
   */
  onPaymentCompleted?: (component: Component, payment: PayPaymentSessionSuccessfulResponse) => void;

  /**
   * Raised when an error occurs. For more information, see the [CheckoutError](https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/flow-library-reference/checkouterror) reference.
   *
   * @param {Component} component - The component that fired the event.
   * @param {CheckoutError} error - The error object.
   */
  onError?: (component: Component, error: CheckoutError) => void;

  /**
   * Raised when you or the customer submit a component.
   *
   * You can use this callback to perform a customized payment submission using the [Submit Payment Session](https://api-reference.checkout.com/#operation/SubmitPaymentSession) endpoint.
   *
   * You must supply the unmodified response body you received from the [Submit Payment Session API](https://api-reference.checkout.com/#operation/SubmitPaymentSession).
   * If no response is returned, Flow will abort the payment submission.
   *
   * @param {Component} component - The component that fired the event.
   * @param {SubmitData} submitData - Data for submitting customized payment session request.
   * @return {Promise<HandleSubmitResponse | void>} - The response from Submit Payment Session API.
   */
  handleSubmit?: (
    component: Component,
    submitData: SubmitData
  ) => Promise<HandleSubmitResponse | void>;

  /**
   * Raised when a wallet payment button is clicked.
   * For example, Apple Pay, Google Pay, or PayPal.
   *
   * You can use this to perform checks before
   * beginning the payment process.
   *
   * @param {Component} component - The component that triggered the callback.
   * @return {Promise<HandleClickResponse | void>} - Whether to continue with the payment process.
   */
  handleClick?: (component: Component) => Promise<HandleClickResponse | void>;
};

/**
 * Options for configuring a component.
 * @interface ComponentOptions
 */
export type ComponentOptions = Prettify<
  SharedComponentOptions & {
    /**
     * Specifies whether to display the payment button in the UI.
     *
     * If you want to handle payment button rendering and interactions outside of Flow, set this to `false`.
     */
    showPayButton?: boolean;
  }
>;

/**
 * Data to pre-populate components with.
 * @interface ComponentData
 */
export type ComponentData = Prettify<{
  /**
   * The name to pre-populate the cardholder name input field for card payments with.
   */
  cardholderName?: string;

  /**
   * The email address used to pre-populate the email input field in the component.
   */
  email?: string;

  /**
   * The [phone country code](https://www.checkout.com/docs/developer-resources/codes/country-codes) used to pre-populate the phone input fields in the component.
   */
  phoneCountryCode?: string;

  /**
   * The phone number used to pre-populate the phone input fields in the component.
   */
  phoneNumber?: string;
}>;

/**
 * Options for configuring an Authentication component.
 * @interface AuthenticationComponentOptions
 */
export type AuthenticationComponentOptions = Prettify<
  ComponentOptions & {
    data?: Pick<ComponentData, 'email'>;
    required?: boolean;
  }
>;

/**
 * Options for configuring a Card component.
 * @interface CardComponentOptions
 */
export type CardComponentOptions = Prettify<
  ComponentOptions & {
    /**
     * Data to pre-populate the card component with.
     */
    data?: Pick<ComponentData, 'cardholderName' | 'email' | 'phoneCountryCode' | 'phoneNumber'>;

    /**
     * The list of accepted card schemes in card payments.
     *
     * This list overrides `componentOptions.flow.acceptedCardSchemes`.
     */
    acceptedCardSchemes?: CardScheme[];

    /**
     * The list of accepted card types in card payments.
     *
     * This list overrides `componentOptions.flow.acceptedCardTypes`.
     */
    acceptedCardTypes?: CardType[];

    /**
     * The position of the cardholder name input field for card payments.
     */
    displayCardholderName?: 'top' | 'bottom' | 'hidden';

    /**
     * The visibility of the card cvv input field for card payments.
     */
    displayCvv?: 'mandatory' | 'hidden';
  }
>;

/**
 * Options for configuring a Card CVV component.
 * @interface CardCvvComponentOptions
 */
export type CardCvvComponentOptions = Prettify<
  ComponentOptions & {
    /**
     * The required length of the card CVV.
     * @defaultValue 3
     */
    cardCvvLength?: 3 | 4;
  }
>;

/**
 * Options for configuring a Remember Me component.
 * @interface RememberMeComponentOptions
 */
export type RememberMeComponentOptions = Prettify<
  ComponentOptions & {
    /**
     * Data to pre-populate the Remember Me component with.
     */
    data?: Pick<ComponentData, 'email' | 'phoneCountryCode' | 'phoneNumber'>;

    /**
     * The list of accepted card schemes in Remember Me payments.
     *
     * This list overrides `componentOptions.flow.acceptedCardSchemes`.
     */
    acceptedCardSchemes?: CardScheme[];
  }
>;

/**
 * Options for configuring a Stored Card component.
 * @interface StoredCardComponentOptions
 */
export type StoredCardComponentOptions = Prettify<
  ComponentOptions & {
    /**
     * The list of accepted card schemes in stored card payments.
     *
     * This list overrides `componentOptions.flow.acceptedCardSchemes`.
     */
    acceptedCardSchemes?: CardScheme[];

    /**
     * The list of accepted card types in stored card payments.
     *
     * This list overrides `componentOptions.flow.acceptedCardTypes`.
     */
    acceptedCardTypes?: CardType[];

    /**
     * Specifies whether to capture the card CVV when using a stored card for payment.
     * Card CVV will always be captured for stored Mada cards.
     *
     * This value overrides `componentOptions.flow.captureCardCvv`.
     *
     * @defaultValue false
     */
    captureCardCvv?: boolean;

    /**
     * The display mode of the stored cards.
     *
     * @defaultValue 'default_only'
     */
    displayMode?: StoredCardDisplayMode;

    /**
     * The display position of the payment method's additional content.
     *
     * This value overrides `componentOptions.flow.displayPaymentAdditionalContent`.
     *
     * @defaultValue 'above_pay_button'
     */
    displayPaymentAdditionalContent?: DisplayPaymentAdditionalContent;

    /**
     * Called when Flow renders the container for a payment method's additional
     * content and requests your integration to inject custom UI into it.
     *
     * Mount your UI inside the provided Element only. If you mount a
     * framework component, return a stable identifier to receive back in
     * `handlePaymentAdditionalContentUnmount` for cleanup. Return `void` if
     * no teardown is required. May be invoked multiple times as the customer
     * switches payment methods.
     *
     * This method overrides `componentOptions.flow.handlePaymentAdditionalContentMount`
     */
    handlePaymentAdditionalContentMount?: HandlePaymentAdditionalContentMount;

    /**
     * Called when Flow requests you to unmount and clean up any custom UI
     * previously mounted via `handlePaymentAdditionalContentMount`.
     *
     * Use this to dispose framework mounts, remove listeners, and revert DOM
     * changes inside the provided Element. The `mountIdentifier` is the value
     * returned by the mount callback and can be used to locate instances for
     * teardown. This callback can safely no-op if nothing was mounted.
     *
     * This method overrides `componentOptions.flow.handlePaymentAdditionalContentUnmount`
     */
    handlePaymentAdditionalContentUnmount?: HandlePaymentAdditionalContentUnmount;
  }
>;

/**
 * Options for configuring a wallet payment component.
 * @interface WalletComponentOptions
 */
export type WalletComponentOptions = Prettify<
  SharedComponentOptions & {
    /**
     * Specifies whether to capture the customer's billing address during the wallet payment flow.
     */
    captureBillingAddress?: boolean;

    /**
     * When the customer triggers an Apple Pay or Google Pay payment submission, this event is raised.
     *
     * You can use the returned object to inspect the payment details they provided and decide whether to continue with the payment. For example, if you do not want to accept American Express or credit card payments.
     *
     * For Apple Pay payments, `authorizeResult` contains the payload from the [`onpaymentauthorized` event](https://developer.apple.com/documentation/applepayontheweb/applepaypaymentauthorizedevent) provided by the Apple Pay Javascript SDK.
     *
     * For Google Pay payments, `authorizeResult` contains the payload from the [`loadPaymentData` response](https://developers.google.com/pay/api/web/reference/response-objects#PaymentData) provided by the Google Pay API.
     *
     * Flow accepts the card if your provided callback doesn't return a response, or you can specify whether to accept or reject the card.
     *
     * @param {Component} component - The component that fired the event.
     * @param authorizeResult - The wallet payment authorization result.
     * @return {Promise<OnAuthorizedResponse | void>} - Whether to continue with the payment process.
     */
    onAuthorized?: (
      component: Component,
      authorizeResult: unknown
    ) => Promise<OnAuthorizedResponse | void>;
  }
>;

/**
 * Apple Pay accepted card schemes.
 */
export type ApplepayCardScheme =
  | CardScheme.Amex
  | CardScheme.CartesBancaires
  | CardScheme.ChinaUnionPay
  | CardScheme.Discover
  | CardScheme.JCB
  | CardScheme.Mada
  | CardScheme.Mastercard
  | CardScheme.Visa;

/**
 * Apple Pay accepted card types.
 */
export type ApplepayCardType = CardType.Credit | CardType.Debit;

/**
 * Options for configuring the Apple Pay component.
 * @interface ApplepayComponentOptions
 */
export type ApplepayComponentOptions = Prettify<
  WalletComponentOptions & {
    /**
     * The list of accepted card schemes in Apple Pay payments.
     *
     * The values in this list are mapped into values supported by the [Apple Pay JS SDK](https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/supportednetworks).
     *
     * This list overrides `componentOptions.flow.acceptedCardSchemes`.
     */
    acceptedCardSchemes?: ApplepayCardScheme[];

    /**
     * The list of accepted card types in Apple Pay payments.
     *
     * The values in this list are mapped into values supported by the [Apple Pay JS SDK](https://developer.apple.com/documentation/applepayontheweb/applepaymerchantcapability).
     *
     * This list overrides `componentOptions.flow.acceptedCardTypes`.
     */
    acceptedCardTypes?: ApplepayCardType[];

    /**
     * Customizes the Apple Pay wallet button appearance.
     *
     * This value must be supported by the [Apple Pay JS SDK](https://developer.apple.com/documentation/applepayontheweb/applepaybuttonstyle).
     */
    buttonColor?: string;

    /**
     * Customizes the Apple Pay wallet button type.
     *
     * This value must be supported by the [Apple Pay JS SDK](https://developer.apple.com/documentation/applepayontheweb/applepaybuttontype).
     */
    buttonType?: string;
  }
>;

/**
 * Google Pay accepted card schemes.
 */
export type GooglepayCardScheme =
  | CardScheme.Amex
  | CardScheme.Discover
  | CardScheme.JCB
  | CardScheme.Mastercard
  | CardScheme.Visa;

/**
 * Google Pay accepted card types.
 */
export type GooglepayCardType = CardType.Credit | CardType.Prepaid;

/**
 * Options for configuring the Google Pay component.
 * @interface GooglepayComponentOptions
 */
export type GooglepayComponentOptions = Prettify<
  WalletComponentOptions & {
    /**
     * The list of accepted card schemes in Google Pay payments.
     *
     * The values in this list are mapped into values supported by the [Google Pay API](https://developers.google.com/pay/api/web/reference/request-objects#CardParameters).
     *
     * This list overrides `componentOptions.flow.acceptedCardSchemes`.
     */
    acceptedCardSchemes?: GooglepayCardScheme[];

    /**
     * The list of accepted card types by Google Pay.
     *
     * The values in this list are mapped into values supported by the [Google Pay API](https://developers.google.com/pay/api/web/reference/request-objects#CardParameters).
     *
     * This list overrides `componentOptions.flow.acceptedCardTypes`.
     */
    acceptedCardTypes?: GooglepayCardType[];

    /**
     * Customizes the Google Pay wallet button appearance. This value must be accepted by the [Google Pay API](https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions).
     */
    buttonColor?: string;

    /**
     * Customizes the Google Pay wallet button type. This value must be accepted by the [Google Pay API](https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions).
     */
    buttonType?: string;
  }
>;

/**
 * Options for configuring a Flow component.
 * @interface FlowComponentOptions
 */
export type FlowComponentOptions = Prettify<
  ComponentOptions &
    WalletComponentOptions & {
      /**
       * Data to pre-populate the Flow component with.
       */
      data?: ComponentData;

      /**
       * The list of accepted card schemes by Flow.
       *
       * This list applies to the `card`, `applepay`, `googlepay`, `remember_me` and `stored_card` payment methods, and can be overridden by the payment method-specific lists.
       *
       * For example, the list `componentOptions.card.acceptedCardSchemes` overrides this list for the card payment method.
       */
      acceptedCardSchemes?: CardScheme[];

      /**
       * The list of accepted card types by Flow.
       *
       * This list applies to the `card`, `applepay`, `googlepay` and `stored_card` payment methods, and can be overridden by the payment method-specific lists.
       *
       * For example, the list `componentOptions.card.acceptedCardTypes` overrides this list for the card payment method.
       */
      acceptedCardTypes?: CardType[];

      /**
       * Specifies whether to capture the card CVV when using a stored card for payment.
       * Card CVV will always be captured for stored Mada cards.
       *
       * This value applies to the `stored_card` payment method, and can be overridden by the payment method-specific value.
       *
       * For example, `componentOptions.stored_card.captureCardCvv` overrides this value for the stored card payment method.
       *
       * @defaultValue false
       */
      captureCardCvv?: boolean;

      /**
       * The display position of the payment method's additional content.
       *
       * This value applies to the Flow component and `stored_card` payment method, and can be overridden by the payment method-specific values.
       *
       * For example, `componentOptions.stored_card.displayPaymentAdditionalContent` overrides this value for the stored card payment method.
       *
       * @defaultValut 'above_pay_button'
       */
      displayPaymentAdditionalContent?: DisplayPaymentAdditionalContent;

      /**
       * Sets whether to automatically expand the first payment method, if there are multiple payment methods.
       */
      expandFirstPaymentMethod?: boolean;

      /**
       * Called when Flow renders the container for a payment method's additional
       * content and requests your integration to inject custom UI into it.
       *
       * Mount your UI inside the provided Element only. If you mount a
       * framework component, return a stable identifier to receive back in
       * `handlePaymentAdditionalContentUnmount` for cleanup. Return `void` if
       * no teardown is required. May be invoked multiple times as the customer
       * switches payment methods.
       */
      handlePaymentAdditionalContentMount?: HandlePaymentAdditionalContentMount;

      /**
       * Called when Flow requests you to unmount and clean up any custom UI
       * previously mounted via `handlePaymentAdditionalContentMount`.
       *
       * Use this to dispose framework mounts, remove listeners, and revert DOM
       * changes inside the provided Element. The `mountIdentifier` is the value
       * returned by the mount callback and can be used to locate instances for
       * teardown. This callback can safely no-op if nothing was mounted.
       */
      handlePaymentAdditionalContentUnmount?: HandlePaymentAdditionalContentUnmount;
    }
>;

/**
 * Options for configuring any type of component.
 * @interface AllComponentOptions
 */
export type AllComponentOptions = Prettify<SharedComponentOptions & WalletComponentOptions>;

/**
 * Response type for a payment session.
 * @interface PaymentSessionResponse
 */
export type PaymentSessionResponse = Record<string, unknown>;

/**
 * Options map for different component names.
 * @interface ComponentOptionsByComponentName
 */
type ComponentOptionsByComponentName = {
  /**
   * Options for Alipay CN.
   */
  alipay_cn: ComponentOptions;

  /**
   * Options for Alipay HK.
   */
  alipay_hk: ComponentOptions;

  /**
   * Options for Alma.
   */
  alma: ComponentOptions;

  /**
   * Options for Apple Pay.
   */
  applepay: ApplepayComponentOptions;

  /**
   * Options for Bancontact.
   */
  bancontact: ComponentOptions;

  /**
   * Options for Benefit.
   */
  benefit: ComponentOptions;

  /**
   * Options for Bizum.
   */
  bizum: ComponentOptions;

  /**
   * Options for Blik.
   */
  blik: ComponentOptions;

  /**
   * Options for Card.
   */
  card: CardComponentOptions;

  /**
   * Options for DANA.
   */
  dana: ComponentOptions;

  /**
   * Options for EPS.
   */
  eps: ComponentOptions;

  /**
   * Options for GCash.
   */
  gcash: ComponentOptions;

  /**
   * Options for Google Pay.
   */
  googlepay: GooglepayComponentOptions;

  /**
   * Options for iDEAL.
   */
  ideal: ComponentOptions;

  /**
   * Options for Kakao Pay.
   */
  kakaopay: ComponentOptions;

  /**
   * Options for Klarna.
   */
  klarna: ComponentOptions;

  /**
   * Options for KNET.
   */
  knet: ComponentOptions;

  /**
   * Options for MB WAY.
   */
  mbway: ComponentOptions;

  /**
   * Options for MobilePay.
   */
  mobilepay: ComponentOptions;

  /**
   * Options for Multibanco.
   */
  multibanco: ComponentOptions;

  /**
   * Options for Octopus.
   */
  octopus: ComponentOptions;

  /**
   * Options for Przelewy24.
   */
  p24: ComponentOptions;

  /**
   * Options for PayNow.
   */
  paynow: ComponentOptions;

  /**
   * Options for PayPal.
   */
  paypal: WalletComponentOptions;

  /**
   * Options for Plaid.
   */
  plaid: ComponentOptions;

  /**
   * Options for QPay.
   */
  qpay: ComponentOptions;

  /**
   * Options for Remember Me.
   */
  remember_me: RememberMeComponentOptions;

  /**
   * Options for SEPA.
   */
  sepa: ComponentOptions;

  /**
   * Options for stc pay.
   */
  stcpay: ComponentOptions;

  /**
   * Options for Stored Card.
   */
  stored_card: StoredCardComponentOptions;

  /**
   * Options for Swish.
   */
  swish: ComponentOptions;

  /**
   * Options for Tabby.
   */
  tabby: ComponentOptions;

  /**
   * Options for Tamara.
   */
  tamara: ComponentOptions;

  /**
   * Options for Touch 'n Go.
   */
  tng: ComponentOptions;

  /**
   * Options for TrueMoney.
   */
  truemoney: ComponentOptions;

  /**
   * Options for TWINT.
   */
  twint: ComponentOptions;

  /**
   * Options for Vipps.
   */
  vipps: ComponentOptions;

  /**
   * Options for WeChat Pay.
   */
  wechatpay: ComponentOptions;

  /**
   * Options for Authentication.
   */
  authentication: AuthenticationComponentOptions;

  /**
   * Options for Card CVV.
   */
  card_cvv: CardCvvComponentOptions;

  /**
   * Options for Shipping Address.
   */
  shipping_address: ComponentOptions;

  /**
   * Options for Flow.
   */
  flow: FlowComponentOptions;
};

/**
 * Options for customising components.
 * @interface CheckoutWebComponentsComponentOptions
 */
type CheckoutWebComponentsComponentOptions = Prettify<
  Partial<ComponentOptionsByComponentName> & {
    /**
     * Data to pre-populate components with.
     */
    data?: ComponentData;
  }
>;

/**
 * Configuration options for the checkout components.
 * @interface Options
 */
export type Options = Prettify<
  {
    /**
     * Your public API key. Your key will be prefixed with `pk_`.
     */
    publicKey: string;

    /**
     * The response from the `PaymentSession`.
     */
    paymentSession: PaymentSessionResponse;

    /**
     * Sets the customer's locale. Explicitly setting this value will override the `locale` returned by the `PaymentSession`.
     * To see a list of supported languages, refer to the [localization](https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/add-localization-to-your-flow-integration) documentation.
     */
    locale?: string;

    /**
     * Sets the environment that the library should point, and send requests to.
     * Use `sandbox` for your test environment, or `production` for your live environment.
     *
     * @defaultValue production
     */
    environment?: EnvironmentUnion;

    /**
     * Allows you to customize the visual appearance of the `CheckoutWebComponents` elements.
     * To see the customization options, refer to the [Appearance options](https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/customize-your-flow-integration#Appearance_options) documentation.
     */
    appearance?: PartialDesignTokens;

    /**
     * Allows you to provide custom text translations for a natively supported languages,
     * or add custom text for locales and languages not natively supported by Flow.
     * To learn how to add translations, refer to the [Add localization to your Flow integration](https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/add-localization-to-your-flow-integration) documentation.
     */
    translations?: CustomTranslations;

    /**
     * Allows you to customize the options for the individual Flow components.
     * For example, you can change the position of the cardholder field in the `card` component.
     * To see the customization options, refer to the [component options](https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/customize-your-flow-integration#Component_options) documentation.
     */
    componentOptions?: CheckoutWebComponentsComponentOptions;
  } & AllComponentOptions
>;

/**
 * Details accepted by `CheckoutWebComponents.update()`.
 *
 * All fields are optional; only the fields you provide will be updated on the internal payment session state.
 * @interface UpdateCheckoutWebComponentsDetails
 */
export type UpdateCheckoutWebComponentsDetails = {
  /**
   * The new payment amount in the minor unit of the session's currency (for example, `1000` = 10.00 in a 2-decimal currency).
   *
   * Must be a non-negative integer. Non-integer, negative, or non-numeric values will cause `update()` to reject with a `CheckoutValidationError`.
   */
  amount?: number;

  /**
   * The new currency for the payment session, as a 3-character uppercase ISO 4217 code (for example, `'GBP'`, `'USD'`, `'EUR'`).
   *
   * Invalid values will cause `update()` to reject with a `CheckoutValidationError`.
   */
  currency?: string;
};

/**
 * Interface for creating checkout web components.
 * @interface CheckoutWebComponents
 */
export interface CheckoutWebComponents {
  /**
   * Creates a new component.
   * @param {T} name - The name of the component.
   * @param {ComponentOptionsByComponentName[T]} options - The options for the component.
   * @returns {Component} The created component.
   */
  create<T extends ComponentNameUnion>(
    name: T,
    options?: ComponentOptionsByComponentName[T]
  ): Component;

  /**
   * Updates `amount` and/or `currency` on the internal payment session state.
   *
   * This method is client-side only and does not make any network request to Checkout.com. Any components already mounted to the DOM will re-render automatically to reflect the new values (for example, an updated total shown by the `card` component).
   *
   * Validation is performed synchronously before the promise resolves:
   * - `amount` must be a non-negative integer expressed in the minor unit of the session's currency.
   * - `currency` must be a 3-character uppercase ISO 4217 code.
   * - Any unknown keys on `details` are rejected.
   *
   * If validation fails, the returned promise rejects with a {@link CheckoutValidationError} (`type: 'Validation'`, `code: 'update_parameters_invalid'`). The `details.validationErrors` property maps each invalid field name to a human-readable reason; unknown keys produce the reason `"not supported field"`.
   *
   * @param {UpdateCheckoutWebComponentsDetails} details - The fields to update. Fields omitted from `details` are left unchanged.
   * @returns {Promise<void>} A promise that resolves once the internal state has been updated and mounted components have been notified.
   * @throws {CheckoutValidationError} If `details` contains invalid values or unsupported fields.
   */
  update(details: UpdateCheckoutWebComponentsDetails): Promise<void>;
}

/**
 * Interface for initializing CheckoutWebComponents.
 * @interface WindowCheckoutWebComponents
 */
export interface WindowCheckoutWebComponents {
  /**
   * Initializes the CheckoutWebComponents.
   * @param {Options} options - The options for initializing CheckoutWebComponents.
   * @returns {CheckoutWebComponents} - The initialized CheckoutWebComponents instance.
   */
  (options: Options): CheckoutWebComponents;
}

/**
 * Loads the checkout web components.
 * @param {Options} options - The options for loading CheckoutWebComponents.
 * @returns {Promise<CheckoutWebComponents>} - A promise that resolves to the loaded CheckoutWebComponents instance.
 */
export type LoadCheckoutWebComponents = (options: Options) => Promise<CheckoutWebComponents>;
